import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '../hooks/useTheme';
import { Gauge, ChevronLeft, ArrowRight } from 'lucide-react';
import CSIAssessmentForm from '../components/CSIAssessmentForm';
import CSIResultSkeleton from '../components/layout/CSIResultSkeleton';
import api from '../api/api';

const CATEGORIES = [
    { name: 'Performance', desc: 'how you show up', color: '#6366f1' },
    { name: 'Skilling', desc: 'how you grow', color: '#0ea5e9' },
    { name: 'Networking', desc: 'who you connect with', color: '#14b8a6' },
    { name: 'Community', desc: 'how you contribute', color: '#f59e0b' },
    { name: 'Feedback', desc: 'how you learn', color: '#ec4899' },
];

const SCALE = [
    { label: 'Strongly Disagree', color: '#ef4444' },
    { label: 'Disagree', color: '#f97316' },
    { label: 'Neutral', color: '#eab308' },
    { label: 'Agree', color: '#84cc16' },
    { label: 'Strongly Agree', color: '#22c55e' },
];

export default function CareerSuccessIndex() {
    const { isDarkMode } = useTheme();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const retake = params.get('retake') === '1';

    // If the user already has a result, send them straight to their report card
    // (unless they explicitly chose to retake). In retake mode we still note the
    // prior result so we can offer a "back to my results" escape hatch.
    const [checking, setChecking] = useState(!retake);
    const [hasPrior, setHasPrior] = useState(false);
    // The assessment opens on an intro screen; the questionnaire mounts on Start.
    const [started, setStarted] = useState(false);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const res = await api.get('/csi/my-result');
                const has = !!(res.data?.result && (res.data.result.results || []).length);
                if (!alive) return;
                if (has && !retake) {
                    navigate('/career-success-index/user/result', { replace: true });
                    return;
                }
                if (has) setHasPrior(true);
            } catch { /* no result / error -> show the assessment */ }
            if (alive) setChecking(false);
        })();
        return () => { alive = false; };
    }, [retake, navigate]);

    const pageBg = `min-h-screen ${isDarkMode ? 'bg-surface' : 'bg-surface'}`;
    const heading = isDarkMode ? 'text-slate-100' : 'text-slate-900';
    const sub = isDarkMode ? 'text-slate-400' : 'text-slate-600';
    const body = isDarkMode ? 'text-slate-300' : 'text-slate-700';
    const card = isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200';

    // While checking for a prior result, show the result skeleton: users who
    // have one get redirected to /result (same skeleton -> seamless, no flash of
    // the assessment skeleton). New users briefly see it before the form loads.
    if (checking) return <CSIResultSkeleton isDarkMode={isDarkMode} />;

    const backToResults = hasPrior && (
        <button
            onClick={() => navigate('/career-success-index/user/result')}
            className={`flex items-center gap-1 mb-4 text-sm font-medium ${isDarkMode ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-800'}`}
        >
            <ChevronLeft size={16} /> Back to my results
        </button>
    );

    // Intro screen (Alicia's copy) — shown before the questionnaire.
    if (!started) {
        return (
            <div className={pageBg}>
                <div className="max-w-3xl mx-auto px-4 py-6 md:py-8">
                    {backToResults}
                    <div className={`rounded-2xl border shadow-sm p-6 md:p-8 ${card}`}>
                        <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-5 ${isDarkMode ? 'bg-primary-900/40 text-primary-400 border border-primary-800' : 'bg-primary-50 text-primary-600 border border-primary-100'}`}>
                            <Gauge size={26} />
                        </div>
                        <h1 className={`text-2xl md:text-3xl font-extrabold tracking-tight ${heading}`}>
                            Take your Career Success Index Assessment
                        </h1>
                        <p className={`mt-2 text-sm ${sub}`}>Quick self-assessment to identify strengths and weaknesses.</p>

                        <h2 className={`mt-7 text-xs font-bold uppercase tracking-wider ${isDarkMode ? 'text-primary-400' : 'text-primary-700'}`}>
                            What you're about to do
                        </h2>
                        <p className={`mt-2.5 text-sm md:text-base ${body}`}>
                            You'll breeze through <span className="font-semibold">10 bite-sized questions</span> in each of our five career-boosting categories:
                        </p>

                        <div className={`mt-3 rounded-xl border overflow-hidden ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                            {CATEGORIES.map((c, i) => (
                                <div
                                    key={c.name}
                                    className={`flex items-center gap-3 px-4 py-3 text-sm ${i < CATEGORIES.length - 1 ? (isDarkMode ? 'border-b border-slate-700' : 'border-b border-slate-200') : ''}`}
                                >
                                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: c.color }} />
                                    <span className={`font-semibold ${heading}`}>{c.name}</span>
                                    <span className={sub}>— {c.desc}</span>
                                </div>
                            ))}
                        </div>
                        <p className={`mt-2.5 text-xs ${sub}`}>Each section takes just a couple of minutes.</p>

                        <p className={`mt-5 text-sm md:text-base ${body}`}>
                            When you finish, you'll get your <span className="font-semibold">Career Success Index Score</span> — a friendly snapshot of where you shine and where you can grow.
                        </p>
                        <p className={`mt-3 text-sm md:text-base ${body}`}>
                            You'll receive a clean, printable version you can save, share with a coach, or use as your roadmap inside the Build My Biz Brand app.
                        </p>

                        <div className={`mt-5 rounded-xl border border-dashed p-4 ${isDarkMode ? 'border-slate-600 bg-slate-900/40' : 'border-slate-300 bg-slate-50'}`}>
                            <p className={`text-sm ${body}`}>
                                Answer each question from <span className="font-semibold">Strongly Disagree (1)</span> to <span className="font-semibold">Strongly Agree (5)</span>, or select <span className="font-semibold">N/A</span>.
                            </p>
                            <div className="mt-3 flex flex-wrap gap-1.5">
                                {SCALE.map((s) => (
                                    <span key={s.label} className="flex-1 min-w-[64px] text-center text-white text-[11px] font-bold rounded-lg px-1 py-1.5 leading-tight" style={{ backgroundColor: s.color }}>
                                        {s.label}
                                    </span>
                                ))}
                                <span className="text-center text-white text-[11px] font-bold rounded-lg px-3 py-1.5" style={{ backgroundColor: '#94a3b8' }}>N/A</span>
                            </div>
                            <p className={`mt-2.5 text-xs ${sub}`}>A response to each question is required to advance to the next step.</p>
                        </div>

                        <button
                            onClick={() => setStarted(true)}
                            className="mt-7 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-primary-600 hover:bg-primary-700 text-white font-semibold py-3.5 transition-colors"
                        >
                            Start assessment <ArrowRight size={18} />
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={pageBg}>
            <div className="max-w-3xl mx-auto px-4 py-6 md:py-8">
                {backToResults}

                {/* Header */}
                <div className="flex items-start gap-3 mb-5">
                    <div className={`p-2 rounded-xl ${isDarkMode ? 'bg-primary-900/50 text-primary-400' : 'bg-primary-50 text-primary-600'}`}>
                        <Gauge size={24} />
                    </div>
                    <div>
                        <h1 className={`text-2xl md:text-3xl font-extrabold tracking-tight ${heading}`}>Career Success Index</h1>
                    </div>
                </div>

                <CSIAssessmentForm onComplete={(data) => navigate('/career-success-index/user/result', { state: { celebrate: true, result: data } })} />
            </div>
        </div>
    );
}
